import dayjs, { Dayjs } from 'dayjs'
import LayerStyles from '../mapping/common/LayerStyles'
import type { Level as ServiceLevel } from './api/Level'
import type { Variable as ServiceVariable } from './api/Variable'
import { Model } from './Model'
import { OverlayLevel } from './overlay/OverlayLevel'
import { RepresentationType } from './RepresentationType'
import type { Variable } from './Variable'
import DateService from '@services/DateService'
import { BoundsLiteral } from 'leaflet'
import { Extent } from './api/Extent'
import { usePersonalizedGroupStore } from 'src/stores/personalizedGroupStore'
import router from 'src/router/index'
import { useFirePersonalizedGroupStore } from 'src/stores/firePersonalizedGroupStore'
import { Routes } from 'src/router/routes'
import { Hours, Minutes } from 'src/typings/time'
import { WeatherTimeResolution } from '@enums/WheatherTimeResolution'

export enum LevelTransversalID {
  TEMP_2M = '2_T',
  TEMP_500HPA = '500_T',
  TEMP_850HPA = '850_T',
  TEMP_MAX_2M = '2MAX_T',
  TEMP_MIN_2M = '2MIN_T',
  TEMP_MEAN_2M = '2MEAN_T',
  GEOPOT_500HPA = '500_G',
  GEOPOT_850HPA = '850_G',
  GEOPOT = '0_G',
  SEA_LEVEL = '0_PRES',
  REL_HUMIDITY_2M = '2_RH',
  REL_HUMIDITY_MAX_2M = '2MAX_RH',
  REL_HUMIDITY_MIN_2M = '2MIN_RH',
  REL_HUMIDITY_MEAN_2M = '2MEAN_RH',
  PREC_1H = '1_P',
  PREC_ACUM_24H = '24_P',
  PREC_AVG_COMARCAS = 'CYL_C_P',
  PREC_ACUM_MONTH = '30D_P',
  SNOW_ACUM_24H = '24_S',
  TRIPLE_30 = '3_30_CEF',
  WIND_SPEED_10M = '10_V',
  WIND_DIR_10M = '10_DV',
  WIND_ARROWS_10M = '10_WIND_ARROWS',
  LOW_COVERAGE = 'B_C',
  ANOM_MAX_T = 'ANOM_MAX_T',
  ANOM_MIN_T = 'ANOM_MIN_T',
  ANOM_MEAN_T = 'ANOM_MEAN_T',
  ANOM_MAX_RH = 'ANOM_MAX_RH',
  ANOM_MIN_RH = 'ANOM_MIN_RH',
  ANOM_MEAN_RH = 'ANOM_MEAN_RH',
  ANOM_PREC = 'ANOM_P',
  ANOW_V = 'ANOM_V',
  EFFIS_EUROPEO_FWI = 'ecmwf007.fwi',
  EFFIS_EUROPEO_ISI = 'ecmwf007.isi',
  EFFIS_EUROPEO_BUI = 'ecmwf007.bui',
  EFFIS_EUROPEO_FFMC = 'ecmwf007.ffmc',
  EFFIS_EUROPEO_DMC = 'ecmwf007.dmc',
  EFFIS_EUROPEO_DC = 'ecmwf007.dc',
  EFFIS_METEOFRANCE_FWI = 'mf010.fwi',
  EFFIS_METEOFRANCE_ISI = 'mf010.isi',
  EFFIS_METEOFRANCE_BUI = 'mf010.bui',
  EFFIS_METEOFRANCE_FFMC = 'mf010.ffmc',
  EFFIS_METEOFRANCE_DMC = 'mf010.dmc',
  EFFIS_METEOFRANCE_DC = 'mf010.dc',
  FWI_AEMET = 'fwi_AEMET'
}

export enum LevelReference {
  RAYOS_D = 'RAYOS_D',
  RAYOS_1 = 'RAYOS_1',
  ESP = 'ESP'
}

export class Level {
  /** UUID */
  id: string
  name: string
  path?: string
  url?: string
  date?: Dayjs
  outDate?: Dayjs
  extent?: BoundsLiteral
  colors: string[]
  intervals: number[]
  outRangeMin: boolean
  outRangeMax: boolean
  units: string
  conversionFunction?: Function
  timeInterval: Minutes
  timeWindow: Hours
  timePrediction?: number
  stepsDelay?: number
  representation: RepresentationType
  styleType: ServiceVariable['STYLE_TYPE']
  style: L.StyleFunction
  /** ID interno de la variable (para filtrados, busquedas etc) */
  variableReference: string
  /** ID de la variable de este level para pedirlo a la api */
  variableLoadId: WeatherTimeResolution | (string & {})
  /** ID que se envia a la api para solicitar el level */
  reference: string
  /** ID para buscar levels equivalentes entre variables */
  transversalId: LevelTransversalID
  isOverlable: boolean
  overlayName?: string
  lastIntervalDisabled: boolean
  tooltip: string
  isReducedModeCompatible: boolean
  observationEquivalent?: string
  variableId?: string
  modelId?: string
  groupId?: string

  constructor(
    serviceLevel: ServiceLevel,
    {
      id: variableId,
      reference: variableReference,
      outRangeMin: variableOutRangeMin,
      outRangeMax: variableOutRangeMax,
      timeInterval: variableTimeInterval,
      timeWindow: variableTimeWindow,
      timePrediction: variableTimePrediction,
      styleType: variableStyleType,
      extent: variableExtent,
      units: variableUnits,
      groupId: variableGroupId,
      idModel: variableModelId,
      representation: variableRepresentation,
      conversionFunction: variableConversionFunction,
      tooltip: variableTooltip
    }: Variable,
    public isFireLevel?: boolean
  ) {
    this.id = serviceLevel.ID_NIVEL ?? serviceLevel.NAME
    this.variableId = variableId
    this.groupId = variableGroupId
    this.modelId = variableModelId
    this.name = serviceLevel.NAME
    this.path = serviceLevel.PATH
    this.url = serviceLevel.URL
    this.reference = serviceLevel.REFERENCE
    if (serviceLevel.EXTENT) {
      this.extent = Extent.toLeafletExtent(serviceLevel.EXTENT)
    } else {
      this.extent = variableExtent
    }
    this.isReducedModeCompatible = Boolean(serviceLevel.SHOW_REDUCED_MODE)
    this.representation = serviceLevel.REPRESENTATION ?? variableRepresentation
    this.timeInterval = serviceLevel.TIME_INTERVAL ?? variableTimeInterval
    this.timeWindow = serviceLevel.TIME_WINDOW ?? variableTimeWindow
    this.timePrediction = serviceLevel.TIME_PREDICTION ?? variableTimePrediction
    this.lastIntervalDisabled = serviceLevel.LAST_INTERVAL_DISABLED
    this.colors = serviceLevel.COLORS
    this.intervals = serviceLevel.INTERVAL
    this.outRangeMin = serviceLevel.OUT_RANGE_MIN ?? variableOutRangeMin
    this.outRangeMax = serviceLevel.OUT_RANGE_MAX ?? variableOutRangeMax
    this.units = serviceLevel.UNIDADES ?? variableUnits
    this.stepsDelay = serviceLevel.STEPS_DELAY
    if (serviceLevel.DATE) {
      this.date = DateService.utc(serviceLevel.DATE, 'YYYY-MM-DD')
      if (serviceLevel.SALIDA) {
        this.outDate = this.date?.hour(+serviceLevel.SALIDA)!
      }
    }
    this.tooltip = serviceLevel.TOOLTIP ?? variableTooltip
    this.isOverlable = serviceLevel.OVERLAY
    this.overlayName = serviceLevel.OVERLAY_NAME

    const serviceConversionFunction =
      serviceLevel.CONVERSION_FUNCTION ??
      serviceLevel.CONVERSION_FUNCTION ??
      (this.units === variableUnits ? variableConversionFunction : null)
    if (typeof serviceConversionFunction === 'function') {
      this.conversionFunction = serviceConversionFunction
    } else {
      try {
        // eslint-disable-next-line no-new-func
        this.conversionFunction = serviceConversionFunction
          ? new Function(
              serviceConversionFunction.arguments,
              serviceConversionFunction.body
            )
          : undefined
      } catch (err) {
        console.error('Error parsing conversion function')
      }
    }

    this.styleType = serviceLevel.STYLE_TYPE ?? variableStyleType
    this.variableReference = variableReference
    this.variableLoadId = serviceLevel.VAR_NAME
    this.transversalId =
      (serviceLevel.LEVEL_IDENTIFIER as LevelTransversalID) ||
      (serviceLevel.VAR_NAME as LevelTransversalID)
    this.observationEquivalent = serviceLevel.OBS_EQ

    if (this.isDiscreteIntervals()) {
      if (serviceLevel.STEPS) {
        this.colors = LayerStyles.calculateColorScale(
          serviceLevel.COLORS,
          serviceLevel.STEPS
        )
      }
      this.style = LayerStyles.automaticStyleDiscreteValues(
        this.intervals,
        this.colors
      )
    } else if (this.isBinaryIntervals()) {
      this.style = LayerStyles.automaticStyleBinary()
    } else {
      if (serviceLevel.INTERVAL && serviceLevel.INTERVAL.length === 2) {
        const intervalColors = LayerStyles.calculateIntervalColors(
          serviceLevel.INTERVAL,
          serviceLevel.COLORS,
          serviceLevel.STEPS
        )
        this.colors = intervalColors.colorScale
        this.intervals = intervalColors.interval
      }
      this.style = LayerStyles.automaticStyle(
        this.intervals,
        this.colors,
        this.outRangeMin,
        this.outRangeMax
      )
    }
  }

  static fromFireVariable(
    variable: Variable,
    model: Model,
    newVariable: Variable
  ) {
    const newLevel = new Level(
      {
        ID_NIVEL: variable.id,
        NAME: variable.name,
        REFERENCE: variable.reference,
        STEPS: variable.steps,
        REPRESENTATION: variable.representation,
        VAR_NAME: model.id,
        PATH: variable.path,
        TIME_INTERVAL: variable.timeInterval,
        TIME_WINDOW: variable.timeWindow,
        COLORS: variable.colors,
        INTERVAL: variable.intervals,
        LAST_INTERVAL_DISABLED: variable.lastIntervalDisable,
        LEVEL_IDENTIFIER: variable.reference,
        CONVERSION_FUNCTION: variable.conversionFunction,
        SHOW_REDUCED_MODE: variable.isReducedModeCompatible,
        URL: variable.url
      } as ServiceLevel,
      {
        ...variable,
        reference: newVariable.reference
      } as Variable,
      true
    )
    newLevel.variableId = model.id
    return newLevel
  }

  isDiscreteIntervals() {
    return this.styleType === 'discrete'
  }

  isBinaryIntervals() {
    return this.styleType === 'binary'
  }

  getIntervalHours() {
    if (this.timeInterval === 0) {
      return 0
    }
    return this.timeInterval / 60
  }

  isSingleBand() {
    return this.timeInterval === 0
  }

  isMinutal() {
    return this.timeInterval < 60
  }

  isHourly() {
    return this.timeInterval === 60
  }

  is12Hourly() {
    return this.timeInterval === 720
  }

  isDaily() {
    return this.timeInterval % 1440 === 0 && this.timeInterval !== 0
  }

  noSteps() {
    return this.timeInterval === 0
  }

  isInSelectedPersonalizedGroup(isFire?: boolean) {
    const isRouteIncendios = router.currentRoute.value.path === Routes.INCENDIOS
    const store =
      isRouteIncendios || isFire
        ? useFirePersonalizedGroupStore()
        : usePersonalizedGroupStore()
    const personalizedGroupSelected = store.personalizedGroupSelected
    if (!personalizedGroupSelected || !personalizedGroupSelected.levels) {
      return true
    } else {
      return personalizedGroupSelected.levels.includes(this.id)
    }
  }

  isReportObservacionLevel() {
    // TODO: comprobacion para saber si es un level obs o pred
    return !this.colors && !this.styleType
  }

  toOverlayLevel(model: Model): OverlayLevel {
    return {
      id: this.id,
      transversalId: this.transversalId,
      level: this,
      model: model
    }
  }

  async getWMTSDateList() {
    let dateList: Dayjs[] = []

    if (this.representation === RepresentationType.TILE_LAYER) {
      if (this.url) {
        try {
          const response = await fetch(`${this.url}&request=GetCapabilities`)
          const text = await response.text()
          const parser = new DOMParser()
          const xmlDoc = parser.parseFromString(text, 'application/xml')

          const layer = Array.from(xmlDoc.getElementsByTagName('Layer')).find(
            (layer) =>
              layer.getElementsByTagName('ows:Title')[0]?.textContent ===
              this.reference
          )

          if (layer) {
            const dimensions = layer.getElementsByTagName('Dimension')
            if (dimensions.length > 0) {
              dateList = Array.prototype.map.call(
                dimensions[0].getElementsByTagName('Value'),
                (x) => {
                  return dayjs.utc(x.textContent, 'YYYY-MM-DD')
                }
              ) as Dayjs[]
            }
          }
        } catch (error) {
          console.error('Error fetching WMTS GetCapabilities:', error)
        }
      }
    }

    return dateList
  }
}

export function instanceOfLevel(object: any): object is Level {
  return 'representation' in object
}
